import {
  Box,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  SelectProps,
  Theme,
  styled,
  useTheme,
} from '@mui/material';
import { useField } from 'formik';
import Checkbox from '../../../components/InvestorReport/checkboxforinvestorreport';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useFieldRequired } from '../FormSchemaContext';

const MultiSelect = styled(Select)(({ theme }) => ({
  '& .MuiOutlinedInput-notchedOutline': {
    borderRadius: "10px",
    backgroundColor: `${theme.palette.common.white}OD`,
    border: `0.5px solid ${theme.palette.text.secondary}80`,
  },
  "& .MuiSelect-select": {
    padding: "15px",
    "& div": {
      ...theme.typography.body_500,
      color: theme.palette.text.primary
    }
  },
  svg: {
    color: theme.palette.text.primary,
  },
  '& .Mui-disabled': {
    cursor: 'not-allowed',
    pointerEvents: 'all',
  },
}));

export type SelectOption = {
  label: string;
  value: any;
};

export type FormSelectProps = SelectProps & {
  name: string;
  options?: SelectOption[];
  isSelectAllOptionEnabled?: boolean
  selectAllLabel?: string
};

const MenuProps = {
  PaperProps: {
    sx: {
      maxWidth: { xs: '100%', sm: '31%', md: '26%' },
      maxHeight: '30%',
      overflowX: 'auto',
      borderRadius: '10px',
    }
  }
};

export default function FormMultiSelect({
  name,
  label,
  options = [],
  disabled,
  isSelectAllOptionEnabled = true,
  selectAllLabel = 'Select All',
  ...rest
}: FormSelectProps): JSX.Element {
  const id = `${name}-input`;
  const labelId = `${id}-label`;
  const errorId = `${id}-error-text`;
  const [field, meta, helpers] = useField(name);
  const { value } = field;
  const { setValue } = helpers;
  const theme = useTheme();
  const errorText = meta.error && meta.touched ? meta.error : '';
  const hasError = Boolean(errorText);
  const isRequired = useFieldRequired(name);
  const ariaLabel =
    rest['aria-label'] ?? (typeof label === 'string' ? label : name);

  const selectAllOption = { label: selectAllLabel, value: '__all__' };
  const allValues = options.map(option => option.value);
  const areAllOptionsSelected = isSelectAllOptionEnabled && allValues.every(val => (value ?? []).includes(val));

  const handleChange = (event: SelectChangeEvent<unknown>) => {
    const selectedValues = event.target.value as Array<string>;

    if (isSelectAllOptionEnabled) {
      if (selectedValues.includes('__all__')) {
        setValue(areAllOptionsSelected ? [] : allValues);
      } else {
        setValue(selectedValues);
      }
    } else {
      setValue(selectedValues);
    }
  };

  return (
    <FormControl
      fullWidth
      error={hasError}
      sx={{ my: { xs: 1, md: 2 } }}
    >
      {label && (
        <InputLabel
          htmlFor={labelId}
          sx={(theme: Theme) => ({
            color: disabled ? `${theme.palette.text.secondary}4D` : theme.palette.text.secondary,
            ...theme.typography.body_500,
          })}
        >
          {label}
        </InputLabel>
      )}
      <MultiSelect
        labelId={labelId}
        id={id}
        multiple
        aria-label={ariaLabel}
        aria-invalid={hasError}
        aria-describedby={hasError ? errorId : undefined}
        inputProps={{
          'aria-label': ariaLabel,
          'aria-invalid': hasError,
          'aria-describedby': hasError ? errorId : undefined,
          'aria-required': isRequired || undefined,
          ...(rest.inputProps || {}),
        }}
        renderValue={(selected: any) => {
          const selectedLabels = selected
            .map((selectedValue: string) =>
              options.find(({ value }) => value === selectedValue)?.label || ''
            );

          const displayText = selectedLabels.join(', ');

          return (
            <Box sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
              width: '100%',
              display: 'block',
              position: 'relative',
            }}>
              {displayText}
            </Box>
          );
        }}
        IconComponent={ExpandMoreIcon}
        MenuProps={MenuProps}
        label={label}
        disabled={disabled}
        {...field}
        {...rest}
        value={value}
        onChange={handleChange}
      >
        {isSelectAllOptionEnabled && (
          <MenuItem
            key={selectAllOption.value}
            value={selectAllOption.value}
            divider
          >
            <ListItemText
              disableTypography
              sx={(theme: Theme) => ({
                fontWeight: 600,
                color: theme.palette.text.primary,
                lineHeight: '20px',
                letterSpacing: '0.02px',
              })}
              primary={selectAllOption.label}
            />
            <Checkbox checked={areAllOptionsSelected} />
          </MenuItem>
        )}
        {options?.map((option, index) => (
          <MenuItem
            sx={{
              '&.MuiMenuItem-root': {
                '&.Mui-selected': {
                  backgroundColor: 'transparent',
                }
              }
            }}
            divider={index !== options.length - 1}
            key={option.value}
            value={option.value}
          >
            <ListItemText
              disableTypography
              sx={(theme: Theme) => ({
                color: theme.palette.text.primary,
                lineHeight: '20px',
                letterSpacing: '0.02px',
                whiteSpace: "normal"
              })}
              primary={option.label}
            />
            <Checkbox checked={(value ?? []).includes(option.value)} />
          </MenuItem>
        ))}
      </MultiSelect>
      {errorText && (
        <FormHelperText
          id={errorId}
          error
          role="alert"
          sx={{ marginLeft: 'unset', color: `${theme.palette.error[900]}`, fontWeight: 500 }}
        >
          {errorText}
        </FormHelperText>
      )}
    </FormControl>
  );
}